/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Formik, Form } from 'formik'
import Events from '../../lib/Events'
import DatePicker from '../../components/ui/DatePicker'
import { useServiceContext } from '../../root'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import { useSearchService } from '../../hooks/useSearchService'
import { Select } from '../../components/Select'
import type { OptionType } from '../../components/Select/Select'
import { InfiniteScrollSelect } from '../../components/InfiniteScrollSelect'
import { FsiLifecycleState, FsiType } from '../../constants/fsi'
import { UPDATED_TYPE, SEARCH_TYPE_V2, API_FIELD_TO_SEARCH_TYPE } from '../../constants/search'
import type { FilterValues } from './types'
import { getDatePickerFormat } from '~/utils/dateFormatting'

interface SearchTableFilterProps {
  isSavedFilterLoading: boolean
  setIsSavedFilterLoading: (loading: boolean) => void
  filterId?: string | number
  phrase: string
  searchType: string
}

export const SearchTableFilter: React.FC<SearchTableFilterProps> = ({
  isSavedFilterLoading,
  setIsSavedFilterLoading,
  filterId,
  phrase,
  searchType
}) => {
  const {
    service: {
      filter: { translate }
    },
    api,
    bus
  } = useServiceContext()

  const tableData = useRcmTableData()
  const { loadSavedFilter } = useSearchService()
  const persistenceKey = tableData.persistenceKey + '_filter'

  // Define initial values constant for reset comparison
  const initialValues: FilterValues = {
    phrase: phrase || '',
    searchType: searchType || 'filename',
    lastUpdatedType: '',
    state: 'ALL',
    itemType: 'ALL',
    updatedFrom: undefined,
    updatedTo: undefined,
    docType: undefined
  }

  const [initialFilterValues, setInitialFilterValues] = useState<FilterValues>(initialValues)

  // Load saved filter on mount if filterId is provided
  useEffect(() => {
    if (filterId && parseInt(filterId.toString()) > 0) {
      loadSavedFilter(filterId).then(async (savedFilter) => {
        if (savedFilter && typeof savedFilter === 'object') {
          // Map fields to searchType (fields can be an array or single value)
          // Saved filters store API field values like 'FILE_NAME', 'CONTENT', 'DOC_UID'
          // Use reverse lookup to convert to UI searchType like 'filename', 'content', 'docUID'
          let mappedSearchType = 'filename'
          if (savedFilter.fields) {
            if (Array.isArray(savedFilter.fields)) {
              // If it's an array, it's 'all' search
              mappedSearchType = 'all'
            } else {
              // Use reverse mapping: API field value -> UI searchType
              mappedSearchType = API_FIELD_TO_SEARCH_TYPE[savedFilter.fields] || 'filename'
            }
          }

          // Load document type if present
          let docTypeOption: OptionType | undefined = undefined
          if (savedFilter.type) {
            try {
              const docType = await api.documentTypes.getById(savedFilter.type, false)
              if (docType && typeof docType === 'object' && 'id' in docType && 'name' in docType) {
                docTypeOption = {
                  value: (docType as any).id,
                  label: (docType as any).name
                }
              }
            } catch (e) {
              console.error('Failed to load document type:', e)
            }
          }

          const filterValues = {
            phrase: savedFilter.phrase || '',
            searchType: mappedSearchType,
            lastUpdatedType: savedFilter.updatedType || '',
            updatedFrom: savedFilter.updatedFrom ? new Date(savedFilter.updatedFrom) : undefined,
            updatedTo: savedFilter.updatedTo ? new Date(savedFilter.updatedTo) : undefined,
            state: savedFilter.status || 'ALL',
            itemType: savedFilter.itemType || 'ALL',
            docType: docTypeOption
          }

          setInitialFilterValues(filterValues)

          // Persist the loaded filter
          persistFilter(filterValues)

          // Update search bar with saved filter's phrase and type
          const updateEvent = Events.UPDATE_SEARCH_BAR_FILTER({
            phrase: savedFilter.phrase || '',
            searchType: mappedSearchType
          })
          bus.dispatchEvent(
            new CustomEvent(updateEvent.type, {
              detail: updateEvent.payload
            })
          )

          // Apply filter to table context and trigger reload
          // This sets the filter data that loadData will use
          tableData.applyFilter(filterValues)
        }
        setIsSavedFilterLoading(false)
      })
    }
  }, [filterId, phrase, searchType]) // Re-run when filterId, phrase, or searchType change to support clicking same saved search multiple times

  // Persist filter values to localStorage
  const persistFilter = (values: FilterValues) => {
    try {
      localStorage.setItem(persistenceKey, JSON.stringify(values))
    } catch (e) {
      console.error('Failed to persist filter:', e)
    }
  }

  // Load persisted filter from localStorage
  useEffect(() => {
    if (!filterId) {
      try {
        const persisted = localStorage.getItem(persistenceKey)
        if (persisted) {
          const parsed = JSON.parse(persisted)
          setInitialFilterValues({
            ...initialFilterValues,
            ...parsed,
            phrase: phrase || '',
            searchType: searchType || 'filename'
          })
        }
      } catch (e) {
        console.error('Failed to load persisted filter:', e)
      }
    }
  }, [])

  const lastUpdatedOptions = [
    { value: '', label: translate('ANYTIME') },
    { value: UPDATED_TYPE.PAST1H, label: translate('PAST_HOUR') },
    { value: UPDATED_TYPE.PAST24H, label: translate('PAST_24_HOURS') },
    { value: UPDATED_TYPE.PAST1W, label: translate('PAST_WEEK') },
    { value: UPDATED_TYPE.PAST1M, label: translate('PAST_MONTH') },
    { value: UPDATED_TYPE.PAST1Y, label: translate('PAST_YEAR') },
    { value: UPDATED_TYPE.CUSTOM, label: translate('CUSTOM_RANGE') }
  ]

  const itemTypeOptions = [
    { value: 'ALL', label: translate('ALL') },
    { value: FsiType.DOCUMENT, label: translate('DOCUMENTS') },
    { value: FsiType.FOLDER, label: translate('FOLDERS') }
  ]

  const stateOptions = [
    { value: 'ALL', label: translate('ALL') },
    { value: FsiLifecycleState.EFFECTIVE, label: translate('STATE_' + FsiLifecycleState.EFFECTIVE) },
    { value: FsiLifecycleState.EXPIRED, label: translate('STATE_' + FsiLifecycleState.EXPIRED) },
    { value: FsiLifecycleState.OBSOLETE, label: translate('STATE_' + FsiLifecycleState.OBSOLETE) }
  ]

  return (
    <div className="filter-wrapper">
      <Formik
        enableReinitialize
        initialValues={initialFilterValues}
        onSubmit={(values) => {
          persistFilter(values)

          // Exit preview mode when applying filters to enable full pagination
          const currentUrl = new URLSearchParams(window.location.search)
          if (currentUrl.get('show') === 'all') {
            currentUrl.delete('show')
            window.history.replaceState({}, '', `/app/Search?${currentUrl.toString()}`)
          }

          // Trigger table reload when filter button is clicked
          tableData.load({
            sortDesc: tableData.sortDesc,
            sortBy: tableData.sortBy,
            pageSize: 10,
            pageStart: 0
          })
        }}
      >
        {({ values, setFieldValue, submitForm, resetForm }) => {
          // Update field value without auto-submitting
          const handleChange = (field: string, value: any) => {
            setFieldValue(field, value)
          }

          const handleReset = () => {
            setInitialFilterValues(initialValues)
            resetForm({ values: initialValues })
          }

          // Check if current values differ from initial values (for Reset button disable state)
          const isValuesEqual = (() => {
            // Exclude phrase and searchType from comparison as they come from URL
            const { phrase: _p1, searchType: _s1, ...compareValues } = values
            const { phrase: _p2, searchType: _s2, ...compareInitial } = initialValues

            return JSON.stringify(compareInitial) === JSON.stringify(compareValues)
          })()

          return (
            <Form>
              <div className="row">
                {/* Column 1: Last Updated + Item Type */}
                <div className="col-xs-4">
                  <div className="form-group" id="06dacd65-516e-43b1-kshi-2aa99a600457">
                    <label className="control-label">{translate('LAST_UPDATED')}</label>
                    <Select
                      id="06dacd65-516e-43b1-kshi-2aa99a600457"
                      value={values.lastUpdatedType}
                      onChange={(option) => {
                        const newValue = (option as OptionType)?.value || ''
                        if (newValue !== 'CUSTOM') {
                          setFieldValue('updatedTo', undefined)
                          setFieldValue('updatedFrom', undefined)
                        }
                        handleChange('lastUpdatedType', newValue)
                      }}
                      options={lastUpdatedOptions}
                      isClearable={false}
                      searchable
                    />
                  </div>

                  {values.lastUpdatedType === 'CUSTOM' && (
                    <div className="pl26">
                      <div className="form-group" id="eed2d707-d8e1-4f3b-8c7b-e53180e8237b">
                        <label className="control-label">{translate('SEARCH_FROM')}</label>
                        <DatePicker
                          name="updatedFrom"
                          id="eed2d707-d8e1-4f3b-8c7b-e53180e8237b"
                          selected={values.updatedFrom}
                          onChange={(date) => handleChange('updatedFrom', date)}
                          className="form-control field-control w100p"
                          placeholder={translate('SELECT_DATE')}
                          maxDate={values.updatedTo || new Date()}
                          showYearDropdown
                          todayButton="Today"
                          dateFormatCalendar="MMMM"
                        />
                      </div>

                      <div className="form-group" id="0f62kke9-341a-43ce-9839-f5d93lsbfabc5">
                        <label className="control-label">{translate('SEARCH_TO')}</label>
                        <DatePicker
                          name="updatedTo"
                          id="0f62kke9-341a-43ce-9839-f5d93lsbfabc5"
                          selected={values.updatedTo}
                          onChange={(date) => handleChange('updatedTo', date)}
                          className="form-control field-control w100p"
                          placeholder={translate('SELECT_DATE')}
                          minDate={values.updatedFrom}
                          maxDate={new Date()}
                          showYearDropdown
                          todayButton="Today"
                          dateFormatCalendar="MMMM"
                        />
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label className="control-label">{translate('ITEM_TYPE_LABEL')}</label>
                    {itemTypeOptions.map((itemType) => (
                      <div key={itemType.value} className="radio classic mb10 w100p">
                        <label>
                          <input
                            type="radio"
                            name="itemType"
                            id={`c749cc0a-b7ad-4387-8044-0e18946b0f97-${itemType.value}`}
                            value={itemType.value}
                            checked={values.itemType === itemType.value}
                            onChange={(e) => handleChange('itemType', e.target.value)}
                          />
                          <div className="px-3">{itemType.label}</div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Column 2: Document Type */}
                <div className="col-xs-4">
                  <div className="form-group" id="k9j74038e-1d5e-42bf-a509-776b48da4b3e">
                    <label className="control-label">{translate('DOC_TYPE')}</label>
                    <InfiniteScrollSelect
                      id="k9j74038e-1d5e-42bf-a509-776b48da4b3e"
                      placeholder={translate('ALL_DOC_TYPES')}
                      name="docType"
                      value={values.docType}
                      onChange={(option) => handleChange('docType', option)}
                      asyncLoadFn={async (search: string, page: number) => {
                        const response = await api.documentTypes.getOptionsForAsyncPicker(search, page)
                        return {
                          options: response.options,
                          hasMore: response.hasMore
                        }
                      }}
                      searchable
                      isClearable={true}
                    />
                  </div>
                </div>

                {/* Column 3: State */}
                <div className="col-xs-4">
                  <div className="form-group">
                    <label className="control-label">{translate('STATE')}</label>
                    {stateOptions.map((state) => (
                      <div key={state.value} className="radio classic mb10 w100p">
                        <label>
                          <input
                            type="radio"
                            name="state"
                            id={`c749cc0a-b7ad-4387-8044-0e18946b0l997-${state.value}`}
                            value={state.value}
                            checked={values.state === state.value}
                            onChange={(e) => handleChange('state', e.target.value)}
                          />
                          <div className="px-3">{state.label}</div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Filter and Clear buttons */}
              <div>
                <button
                  type="submit"
                  data-action="filter"
                  disabled={tableData?.isLoading}
                  className="btn btn-primary"
                  id="b55d4dba-38b9-484c-b924-7b383c0da055"
                >
                  {translate('BTN_FILTER')}
                </button>
                <button
                  type="button"
                  data-action="reset"
                  disabled={isValuesEqual}
                  className="btn btn-chita btn-link"
                  onClick={handleReset}
                  id="83b562da-10ae-4b07-bc8b-468898233b"
                >
                  {translate('BTN_CLEAR_FILTER')}
                </button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}
